import type { ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";

export interface WorkbenchMetricItem {
  id: string;
  label: ReactNode;
  value: ReactNode;
  progress?: number;
  description?: ReactNode;
}

export type WorkbenchMetricGridVariant = "bars" | "cards" | "list";
export type WorkbenchMetricGridDensity = "compact" | "comfortable";

export interface WorkbenchMetricGridProps {
  items: WorkbenchMetricItem[];
  variant?: WorkbenchMetricGridVariant;
  density?: WorkbenchMetricGridDensity;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
  itemClassName?: string;
}

const normalizeProgress = (value: number | undefined) => {
  if (!Number.isFinite(value)) return undefined;
  return Math.min(1, Math.max(0, Number(value)));
};

export function WorkbenchMetricGrid({
  items,
  variant = "bars",
  density = "comfortable",
  emptyState = null,
  ariaLabel = "Metrics",
  className,
  itemClassName,
}: WorkbenchMetricGridProps) {
  if (!items.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  if (variant === "list") {
    return (
      <dl
        aria-label={ariaLabel}
        data-monkeys-component="workbench-metric-grid"
        data-metric-variant={variant}
        data-metric-density={density}
        className={cn(
          "grid min-w-0",
          density === "compact" ? "gap-2" : "gap-3",
          className,
        )}
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex min-w-0 items-start justify-between gap-4",
              density === "compact" ? "text-xs" : "text-sm",
              itemClassName,
            )}
          >
            <dt className="min-w-0 text-muted-foreground">
              <span className="block truncate">{item.label}</span>
              {hasRenderableNode(item.description) ? (
                <span className="mt-0.5 block text-[0.6875rem] leading-4 text-muted-foreground/80">
                  {item.description}
                </span>
              ) : null}
            </dt>
            <dd className="shrink-0 font-semibold tabular-nums text-foreground">
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  if (variant === "cards") {
    return (
      <dl
        aria-label={ariaLabel}
        data-monkeys-component="workbench-metric-grid"
        data-metric-variant={variant}
        data-metric-density={density}
        className={cn(
          "grid min-w-0 [grid-template-columns:repeat(auto-fit,minmax(9rem,1fr))]",
          density === "compact" ? "gap-2" : "gap-3",
          className,
        )}
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              "min-w-0 rounded-lg border border-border/70 bg-card text-card-foreground",
              density === "compact" ? "p-3" : "p-4",
              itemClassName,
            )}
          >
            <dt className="truncate text-xs font-medium text-muted-foreground">
              {item.label}
            </dt>
            <dd
              className={cn(
                "font-semibold tabular-nums text-foreground",
                density === "compact" ? "mt-1.5 text-xl" : "mt-2 text-2xl",
              )}
            >
              {item.value}
            </dd>
            {hasRenderableNode(item.description) ? (
              <div className="mt-2 text-xs leading-5 text-muted-foreground">
                {item.description}
              </div>
            ) : null}
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl
      aria-label={ariaLabel}
      data-monkeys-component="workbench-metric-grid"
      data-metric-variant={variant}
      data-metric-density={density}
      className={cn(
        "grid min-w-0 [grid-template-columns:repeat(auto-fit,minmax(5.5rem,1fr))]",
        density === "compact" ? "gap-2" : "gap-3",
        className,
      )}
    >
      {items.map((item) => {
        const progress = normalizeProgress(item.progress);
        return (
          <div key={item.id} className={cn("min-w-0", itemClassName)}>
            <div
              role={progress === undefined ? undefined : "meter"}
              aria-label={
                progress === undefined ? undefined : String(item.label)
              }
              aria-valuemin={progress === undefined ? undefined : 0}
              aria-valuemax={progress === undefined ? undefined : 100}
              aria-valuenow={
                progress === undefined ? undefined : Math.round(progress * 100)
              }
              className={cn(
                "overflow-hidden rounded-full bg-muted",
                density === "compact" ? "h-1.5" : "h-2",
              )}
            >
              {progress === undefined ? null : (
                <span
                  className="block h-full rounded-full bg-primary transition-[width] duration-200 motion-reduce:transition-none"
                  style={{
                    width: `${Math.max(progress > 0 ? 4 : 0, progress * 100)}%`,
                  }}
                />
              )}
            </div>
            <div className="mt-1.5 flex min-w-0 items-baseline justify-between gap-2">
              <dt className="truncate text-[0.6875rem] text-muted-foreground">
                {item.label}
              </dt>
              <dd className="shrink-0 text-xs font-semibold tabular-nums text-foreground">
                {item.value}
              </dd>
            </div>
          </div>
        );
      })}
    </dl>
  );
}
